import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid';

// Semantic variants are the preferred API. Color-keyed variants are kept
// for back-compat with pages not yet migrated to semantic naming. See
// ui/docs/simkura-design.md — Components / Chip.
//   positive  — healthy / succeeded / online                (green)
//   warning   — needs attention / pending / mid-state       (yellow)
//   critical  — failed / offline / denied / error           (red)
//   info      — informational distinction, no qualitative   (blue)
//   neutral   — N/A, unknown, muted                         (gray)
type SemanticVariant = 'positive' | 'warning' | 'critical' | 'info' | 'neutral';
type ColorVariant = 'green' | 'red' | 'yellow' | 'blue' | 'purple' | 'gray';
type StatusVariant = SemanticVariant | ColorVariant;

interface StatusChipProps {
  variant?: StatusVariant;
}

export const StatusChip = styled('span', {
  shouldForwardProp: prop => isPropValid(prop) && prop !== 'variant'
})<StatusChipProps>`
  display: inline-flex;
  align-items: center;
  padding: 2px ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.border.radius.pill};
  font-size: ${({ theme }) => theme.font.size.xs};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  letter-spacing: 0.4px;
  line-height: 1.4;
  white-space: nowrap;

  ${({ variant, theme }) => {
    switch (variant) {
      case 'positive':
      case 'green':
        return `
          background: ${theme.colorBg.positive};
          color: ${theme.color.green};
        `;
      case 'critical':
      case 'red':
        return `
          background: ${theme.colorBg.critical};
          color: ${theme.color.red};
        `;
      case 'warning':
      case 'yellow':
        return `
          background: ${theme.colorBg.warning};
          color: ${theme.color.yellow};
        `;
      case 'info':
      case 'blue':
      case 'purple':  // legacy purple usages map to info
        return `
          background: ${theme.colorBg.info};
          color: ${theme.color.blue};
        `;
      case 'neutral':
      case 'gray':
      default:
        return `
          background: ${theme.colorBg.neutral};
          color: ${theme.font.color.tertiary};
        `;
    }
  }}
`;

interface DeployedBadgeProps {
  deployed: boolean;
}

export function DeployedBadge({ deployed, ...props }: DeployedBadgeProps) {
  return (
    <StatusChip variant={deployed ? 'positive' : 'neutral'} {...props}>
      {deployed ? 'Yes' : 'No'}
    </StatusChip>
  );
}
